import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';

const COMPONENT_INFO = {
    observation_flat: { label: 'Observation (flat)', unit: 'per_observation', description: 'Flat charge per call observation ingested' },
    scenario_flat: { label: 'Scenario Run (flat)', unit: 'per_run', description: 'Flat charge per scenario run' },
    llm_metric: { label: 'LLM Metric Evaluation', unit: 'per_metric', description: 'Per LLM metric evaluated' },
    non_llm_metric: { label: 'Non-LLM Metric Evaluation', unit: 'per_metric', description: 'Per non-LLM metric evaluated' },
};

const AdminPricing = () => {
    const [globalRules, setGlobalRules] = useState([]);
    const [overrides, setOverrides] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [msg, setMsg] = useState({ type: '', text: '' });

    const [showOverrideForm, setShowOverrideForm] = useState(false);
    const [overrideClientId, setOverrideClientId] = useState('');
    const [overrideComponent, setOverrideComponent] = useState('observation_flat');
    const [overrideRate, setOverrideRate] = useState('');
    const [overrideSubmitting, setOverrideSubmitting] = useState(false);
    const [clients, setClients] = useState([]);

    useEffect(() => { fetchRules(); fetchClients(); }, []);

    const fetchRules = async () => {
        try {
            const res = await adminAPI.getPricingRules();
            setGlobalRules(res.data.global || []);
            setOverrides(res.data.overrides || []);
        } catch (err) {
            console.error('Failed to fetch pricing rules:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchClients = async () => {
        try {
            const res = await adminAPI.getBillingAccounts();
            const allClients = (res.data || []).flatMap(a => a.clients);
            setClients(allClients);
        } catch (err) {
            console.error('Failed to fetch clients:', err);
        }
    };

    const handleSave = async (rule) => {
        setMsg({ type: '', text: '' });
        const val = parseFloat(editValue);
        if (isNaN(val) || val < 0) { setMsg({ type: 'error', text: 'Rate must be non-negative' }); return; }
        try {
            await adminAPI.updatePricingRule(rule.id, { credits_per_unit: val });
            setEditing(null);
            setMsg({ type: 'success', text: 'Pricing updated' });
            fetchRules();
        } catch (err) {
            setMsg({ type: 'error', text: err.response?.data?.error || 'Failed to update' });
        }
    };

    const [deletingOverride, setDeletingOverride] = useState(null);

    const handleDeleteOverride = async (id) => {
        setMsg({ type: '', text: '' });
        try {
            await adminAPI.deletePricingRule(id);
            setMsg({ type: 'success', text: 'Override removed. Global rate will apply.' });
            setDeletingOverride(null);
            fetchRules();
        } catch (err) {
            console.error('Delete override failed:', err);
            setMsg({ type: 'error', text: err.response?.data?.error || err.message || 'Failed to remove override' });
        }
    };

    const handleCreateOverride = async (e) => {
        e.preventDefault();
        console.log('[AdminPricing] handleCreateOverride fired', { overrideClientId, overrideComponent, overrideRate });
        const rate = parseFloat(overrideRate);
        if (!overrideClientId || isNaN(rate) || rate < 0) { setMsg({ type: 'error', text: 'Select a client and enter a valid rate' }); return; }
        setOverrideSubmitting(true);
        setMsg({ type: '', text: '' });
        try {
            const info = COMPONENT_INFO[overrideComponent];
            await adminAPI.createPricingRule({ component: overrideComponent, unit: info.unit, credits_per_unit: rate, client_id: parseInt(overrideClientId) });
            setMsg({ type: 'success', text: 'Client override created' });
            setShowOverrideForm(false);
            setOverrideClientId('');
            setOverrideRate('');
            fetchRules();
        } catch (err) {
            setMsg({ type: 'error', text: err.response?.data?.error || 'Failed to create override' });
        } finally {
            setOverrideSubmitting(false);
        }
    };

    if (loading) return <div className="text-muted-foreground">Loading...</div>;

    return (
        <div>
            <h1 className="text-2xl font-bold text-foreground mb-6">Pricing Rules</h1>

            {msg.text && (
                <div className={`mb-4 rounded-lg px-4 py-2.5 text-sm ${msg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                    {msg.text}
                </div>
            )}

            {/* Global Defaults */}
            <div className="bg-card rounded-lg border border-border mb-8">
                <div className="px-4 py-3 border-b border-border">
                    <h2 className="text-sm font-semibold text-foreground">Global Default Rates</h2>
                    <p className="text-sm text-muted-foreground mt-0.5">These apply to all clients unless overridden.</p>
                </div>
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-muted/50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Component</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Unit</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Credits / Unit</th>
                            <th className="px-6 py-3 w-32"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {globalRules.map((rule) => {
                            const info = COMPONENT_INFO[rule.component] || {};
                            return (
                                <tr key={rule.id}>
                                    <td className="px-4 py-3">
                                        <div className="text-sm font-medium text-foreground">{info.label || rule.component}</div>
                                        <div className="text-sm text-muted-foreground">{info.description}</div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-muted-foreground">{rule.unit.replace(/_/g, ' ')}</td>
                                    <td className="px-4 py-3 text-right">
                                        {editing === rule.id ? (
                                            <input type="number" step="0.0001" min="0" value={editValue} onChange={(e) => setEditValue(e.target.value)} className="w-28 text-right rounded-md border-input shadow-sm focus:border-black focus:ring-black text-sm" autoFocus onKeyDown={(e) => e.key === 'Enter' && handleSave(rule)} />
                                        ) : (
                                            <span className="text-sm font-semibold text-foreground">{Number(rule.credits_per_unit).toFixed(4)}</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        {editing === rule.id ? (
                                            <div className="flex gap-2 justify-end">
                                                <button onClick={() => handleSave(rule)} className="text-sm text-green-600 hover:text-green-800 font-medium">Save</button>
                                                <button onClick={() => setEditing(null)} className="text-sm text-muted-foreground hover:text-foreground">Cancel</button>
                                            </div>
                                        ) : (
                                            <button onClick={() => { setEditing(rule.id); setEditValue(rule.credits_per_unit); }} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">Edit</button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Client Overrides */}
            <div className="bg-card rounded-lg border border-border">
                <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                    <div>
                        <h2 className="text-sm font-semibold text-foreground">Client Overrides</h2>
                        <p className="text-sm text-muted-foreground mt-0.5">Custom pricing for specific clients.</p>
                    </div>
                    <button onClick={() => setShowOverrideForm(!showOverrideForm)} className="px-4 py-2 text-sm font-medium rounded-md text-white bg-black hover:bg-gray-800">
                        {showOverrideForm ? 'Cancel' : '+ Add Override'}
                    </button>
                </div>

                {showOverrideForm && (
                    <div className="px-4 py-3 border-b border-border bg-muted/50">
                        <form onSubmit={handleCreateOverride} className="flex items-end gap-3">
                            <div className="w-52">
                                <label className="block text-sm font-medium text-foreground mb-1">Client</label>
                                <select value={overrideClientId} onChange={(e) => setOverrideClientId(e.target.value)} className="block w-full rounded-md border-input shadow-sm focus:border-black focus:ring-black text-sm" required>
                                    <option value="">Select client</option>
                                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div className="w-52">
                                <label className="block text-sm font-medium text-foreground mb-1">Component</label>
                                <select value={overrideComponent} onChange={(e) => setOverrideComponent(e.target.value)} className="block w-full rounded-md border-input shadow-sm focus:border-black focus:ring-black text-sm">
                                    {Object.entries(COMPONENT_INFO).map(([key, info]) => (
                                        <option key={key} value={key}>{info.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="w-36">
                                <label className="block text-sm font-medium text-foreground mb-1">Credits / Unit</label>
                                <input type="number" step="0.0001" min="0" value={overrideRate} onChange={(e) => setOverrideRate(e.target.value)} className="block w-full rounded-md border-input shadow-sm focus:border-black focus:ring-black text-sm" placeholder="0.0000" required />
                            </div>
                            <button type="submit" disabled={overrideSubmitting} className="px-4 py-2 text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50">
                                {overrideSubmitting ? 'Creating...' : 'Create'}
                            </button>
                        </form>
                    </div>
                )}

                {overrides.length === 0 && !showOverrideForm ? (
                    <div className="px-6 py-8 text-center text-sm text-muted-foreground">
                        No client-specific overrides. All clients use global rates.
                    </div>
                ) : overrides.length > 0 && (
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-muted/50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Client</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Component</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Credits / Unit</th>
                                <th className="px-6 py-3 w-24"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {overrides.map((rule) => (
                                <tr key={rule.id}>
                                    <td className="px-4 py-3 text-sm font-medium text-foreground">{rule.client?.name || '—'}</td>
                                    <td className="px-4 py-3 text-sm text-muted-foreground">{COMPONENT_INFO[rule.component]?.label || rule.component}</td>
                                    <td className="px-4 py-3 text-right text-sm font-semibold text-foreground">{Number(rule.credits_per_unit).toFixed(4)}</td>
                                    <td className="px-4 py-3 text-right">
                                        {deletingOverride === rule.id ? (
                                            <div className="flex gap-2 justify-end">
                                                <button onClick={() => handleDeleteOverride(rule.id)} className="text-sm text-red-600 hover:text-red-800 font-medium">Confirm</button>
                                                <button onClick={() => setDeletingOverride(null)} className="text-sm text-muted-foreground hover:text-foreground">Cancel</button>
                                            </div>
                                        ) : (
                                            <button onClick={() => setDeletingOverride(rule.id)} className="text-sm text-red-600 hover:text-red-800 font-medium">Remove</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default AdminPricing;
